import { Alert, Box, IconButton, Stack } from '@mui/material';
import { memo, ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Flow } from '@thive/fddm-types';
import { RemoveIcon } from '@/assets/icons';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { PropertiesTitle } from '@common/components/PropertiesTitle';
import { useAppStore } from '@/modules/app/store/useAppStore';

import { useFlowUpdaters } from '../hooks/useFlowUpdaters';
import { getFlowTooltipKey } from '../helpers';
import { InvalidFlow } from '../validation/types';
import { GoodsToPersonsProperties } from './GoodsToPersonsProperties';
import ExternalFlowProperties from './ExternalFlowProperties';
import {
  FlowDirection,
  TransportProperties,
  ConsolidationStorageProperties,
  ConsolidationRetrievalProperties,
} from '.';
import { Icon } from '@/modules/toolbar';
import { assertNever } from '@/helpers/utils';

type Props = {
  flow: Flow;
  tooltipTitle: string;
  onDelete: (flow: Flow) => void;
  error?: InvalidFlow;
};

const FlowCardComponent = ({ flow, tooltipTitle, onDelete, error }: Props) => {
  const { t } = useTranslation();
  const { id } = flow;
  const { onNameChange } = useFlowUpdaters(id);
  const mode = useAppStore((state) => state.mode);
  const disabled = mode !== 'EDITABLE';

  const onRemoveClick = useCallback(() => {
    onDelete(flow);
  }, [onDelete, flow]);

  if (!flow) return null;

  const tooltipKey = getFlowTooltipKey(flow.type);

  const getFlowIcon = (type: Flow['type']): ReactElement | null => {
    switch (type) {
      case 'TRANSPORT':
        return <Icon icon='transport_flow' />;

      case 'CONSOLIDATION_STORAGE':
      case 'CONSOLIDATION_RETRIEVAL':
        return <Icon icon='consolidation_flow' />;

      case 'GOODS_TO_PERSONS':
        return <Icon icon='goods_to_persons_flow' />;

      case 'EXTERNAL':
        return <Icon icon='external_flow' />;

      default:
        return assertNever(type);
    }
  };

  return (
    <Stack spacing={1} sx={{ flexDirection: 'column', mb: 2 }}>
      <Stack direction='row' width='100%' alignItems='center' gap={1}>
        <StyledTooltip titleKey={tooltipKey}>
          <Box
            sx={{
              width: 24,
              height: 24,
              padding: 1,
              borderRadius: 1,
              paddingY: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                fontSize: 24,
              },
            }}
          >
            {getFlowIcon(flow.type)}
          </Box>
        </StyledTooltip>
        <PropertiesTitle
          editable={!disabled}
          fontSize={14}
          value={flow.name}
          sx={{ flex: '1 1 auto', alignItems: 'center' }}
          onChange={onNameChange}
        />
        <StyledTooltip title={tooltipTitle} disabled={disabled}>
          <IconButton
            onClick={onRemoveClick}
            sx={{ marginY: 'auto', p: 0, flex: '0 0 auto' }}
            disabled={disabled}
          >
            <RemoveIcon />
          </IconButton>
        </StyledTooltip>
      </Stack>
      <FlowDirection flow={flow} />

      {flow.type === 'TRANSPORT' && <TransportProperties id={id} disabled={disabled} flow={flow} />}
      {flow.type === 'CONSOLIDATION_STORAGE' && (
        <ConsolidationStorageProperties id={id} disabled={disabled} flow={flow} />
      )}

      {flow.type === 'CONSOLIDATION_RETRIEVAL' && (
        <ConsolidationRetrievalProperties id={id} disabled={disabled} flow={flow} />
      )}

      {flow.type === 'GOODS_TO_PERSONS' && (
        <GoodsToPersonsProperties id={id} disabled={disabled} flow={flow} />
      )}

      {flow.type === 'EXTERNAL' && (
        <ExternalFlowProperties id={id} disabled={disabled} flow={flow} />
      )}

      {error?.reason && (
        <Alert severity='error'>
          {t(`errors:flow.${error.reason}`, {
            ...error.context,
          })}
        </Alert>
      )}
    </Stack>
  );
};

export const FlowCard = memo(FlowCardComponent);
